import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {DataFrame, DataFrameRow} from 'data-frames/DataFrameTypes';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import {DatasetUploadStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import toColumnLabel from 'notebook/structured-tables/columns/toColumnLabel';
import {StructuredTableKind, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook, {
  buildComputedTable,
} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import getPlainTextTable from 'notebook/tables/export/getPlainTextTable';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY, GRAY_LIGHTER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import AppCanvasStructuredTableAdapter, {
  AppCanvasStructuredTable,
  AppCanvasStructuredTableMetadata,
  AppCanvasStructuredTableRowMetadata,
} from './AppCanvasStructuredTableAdapter';

export interface TableUiBlockProps {
  id: string;
  userDefinedId: string;
  canvasId: string;
  datasetId: string;
  datasetUploadStatus: DatasetUploadStatus;
  name: string;
  enabled: boolean;
  canvasEnabled: boolean;
  onTableUpdate: (plainTextTable: Array<Array<string>>, tableId: string, dataframeName: string) => void;
}

export function TableLoading(): JSX.Element {
  return (
    <div className={css(styles.tableLoadingBlock)}>
      <span className={css(styles.loadingText)}>Loading table...</span>
      <CenteredHorizontalThrobber />
    </div>
  );
}

export function TableLoadingOrFailed(props: {uploadStatus: DatasetUploadStatus}): JSX.Element {
  return props.uploadStatus === DatasetUploadStatus.IN_PROGRESS ? (
    <TableLoading />
  ) : (
    <InlineBanner bannerType={InlineBannerType.WARNING} text="Invalid dataset provided for table block" />
  );
}

export default function TableUiBlock(props: TableUiBlockProps): JSX.Element {
  return props.datasetId && props.datasetUploadStatus === DatasetUploadStatus.SUCCEEDED ? (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <TableLoading />}
      variables={{
        dataFrameId: props.datasetId,
        limit: getMaxRowsConfig(true, StructuredTableKind.APP_CANVAS),
      }}
    >
      {({dataFrame: dataFrameNode}) =>
        isInaccessible(dataFrameNode) ? null : (
          <TableUiBlockInner {...props} dataframe={dataFrameNode.dataFrameJson} />
        )
      }
    </BenchlingQuery>
  ) : (
    <TableLoadingOrFailed uploadStatus={props.datasetUploadStatus} />
  );
}

export interface TableUiBlockInnerProps {
  id: string;
  userDefinedId: string;
  canvasId: string;
  dataframe: DataFrame;
  name: string;
  enabled: boolean;
  canvasEnabled: boolean;
  onTableUpdate: (plainTextTable: Array<Array<string>>, tableId: string, dataframeName: string) => void;
}

function TableUiBlockInner(props: TableUiBlockInnerProps): JSX.Element {
  const [columnWidths, setColumnWidths] = useState<{[idx: number]: number}>({});
  const dataframe = props.dataframe;
  const adapter = AppCanvasStructuredTableAdapter.getAdapter();
  const tableMetadata: AppCanvasStructuredTableMetadata = {columns: dataframe.columns, columnWidths};
  const table = adapter.createTable({
    name: props.name,
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  const tableCid = `${props.canvasId}-${props.id}`;
  const columnKeyByIndex = objectFromEntries(
    table.columns.map((_, i) => [i, dataframe.columns[i].systemName])
  );
  const numColumns = Object.keys(columnKeyByIndex).length;

  function buildInitialTableRows(): Array<StructuredTableRow<AppCanvasStructuredTableRowMetadata>> {
    return dataframe.rows.map((dataframeRow: DataFrameRow, i) => {
      const row = adapter.getBlankRow(tableMetadata, i, table.columns);
      for (let cellIndex = 0; cellIndex < numColumns; cellIndex++) {
        const columnKey = columnKeyByIndex[cellIndex];
        row.cells[cellIndex] = {text: String(dataframeRow[columnKey] ?? '')};
      }
      return row;
    });
  }

  function handleTableChange(newTable: AppCanvasStructuredTable): void {
    const newColumnWidths: {[idx: number]: number} = {};
    for (const [idx, col] of newTable.columns.entries()) {
      newColumnWidths[idx] = col.width;
    }
    setColumnWidths(newColumnWidths);
    updateRows(newTable.rows);

    const structuredTable = {...newTable, cid: tableCid};
    const computedTable = buildComputedTable(structuredTable, null);
    const plainTextTable = getPlainTextTable(
      adapter.toTable(structuredTable).table,
      computedTable,
      false,
      structuredTable.columns.map(
        (structuredTableColumn) =>
          toColumnLabel({structuredTableColumn, schemas: structuredTable.schemas}) || 'unknown'
      )
    );
    props.onTableUpdate(plainTextTable, props.userDefinedId, dataframe.name);
  }

  const [rows, updateRows] =
    useState<Array<StructuredTableRow<AppCanvasStructuredTableRowMetadata>>>(buildInitialTableRows());

  table.rows = rows;

  return (
    <StructuredTableOutsideNotebook
      structuredTable={{...table, cid: tableCid}}
      cid={tableCid}
      updateHandler={handleTableChange}
      hideFormulaBox={true}
      includeUnsubmittedStatus={false}
      canRename={false}
      locked={!(props.enabled && props.canvasEnabled)}
      enableFormulas={true}
    />
  );
}

const styles = StyleSheet.create({
  tableLoadingBlock: {
    border: `1px solid ${GRAY}`,
    padding: '16px',
    paddingTop: '25px',
    backgroundColor: `${GRAY_LIGHTER}`,
  },
  loadingText: {
    display: 'block',
    textAlign: 'center',
    fontStyle: 'italic',
  },
});
